import React, { useEffect, useState } from "react";
import { changePasswordAPI } from "../api/user.api";
import { setDocsCookie } from "../api/axios";

interface ChangePasswordModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const ChangePasswordModal: React.FC<ChangePasswordModalProps> = ({ isOpen, onClose }) => {
  const [oldPassword, setOldPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMsg(null);
    setSuccessMsg(null);

    if (!oldPassword) {
      setErrorMsg("Please enter your current password");
      return;
    }
    if (!newPassword) {
      setErrorMsg("Please enter a new password");
      return;
    }
    if (newPassword !== confirmPassword) {
      setErrorMsg("New passwords do not match");
      return;
    }

    setIsLoading(true);
    try {
      const res = await changePasswordAPI({
        oldPassword: oldPassword,
        password: newPassword,
      });
      // Server revokes every refresh token on password change and returns a
      // fresh pair — store it so this session stays logged in.
      if (res?.accessToken) {
        window.localStorage.setItem("access_token", res.accessToken);
        setDocsCookie(res.accessToken);
      }
      if (res?.refreshToken) {
        window.localStorage.setItem("refresh_token", res.refreshToken);
      }
      setSuccessMsg("Password changed successfully!");
      setOldPassword("");
      setNewPassword("");
      setConfirmPassword("");
      setTimeout(() => {
        onClose();
        setSuccessMsg(null);
      }, 1500);
    } catch (err: any) {
      const msg = err?.response?.data?.message || err?.message || "Failed to change password";
      if (msg.includes("Wrong current password")) {
        setErrorMsg("Current password is incorrect");
      } else {
        setErrorMsg(msg);
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Close on Escape while open.
  useEffect(() => {
    if (!isOpen || isLoading) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isOpen, isLoading, onClose]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[999] flex items-center justify-center p-4">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-ink-1000/40 backdrop-blur-sm transition-opacity duration-300"
        onClick={onClose}
      />
      
      {/* Modal Content */}
      <div className="relative bg-white w-full max-w-md rounded-3xl border border-ink-100 shadow-floaty overflow-hidden z-10 transition-transform duration-300 scale-100">
        
        {/* Header */}
        <div className="px-6 pt-6 pb-4 border-b border-ink-100 flex items-center justify-between">
          <div>
            <h3 className="font-display font-black text-xl text-ink-1000 leading-tight">
              Change password
            </h3>
            <p className="text-xs text-ink-500 mt-1">
              Update your account credentials safely
            </p>
          </div>
          <button 
            onClick={onClose}
            className="w-9 h-9 rounded-full flex items-center justify-center text-ink-700 hover:bg-ink-100 transition-colors"
            aria-label="Close modal"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Form Body */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          
          {/* Old Password */}
          <div className="field-v2">
            <input
              id="oldPassword"
              type="password"
              required
              placeholder=" "
              value={oldPassword}
              onChange={(e) => setOldPassword(e.target.value)}
            />
            <label htmlFor="oldPassword">Current password</label>
          </div>

          {/* New Password */}
          <div className="field-v2">
            <input
              id="newPassword"
              type="password"
              required
              placeholder=" "
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
            />
            <label htmlFor="newPassword">New password</label>
          </div>

          {/* Confirm New Password */}
          <div className="field-v2">
            <input
              id="confirmPassword"
              type="password"
              required
              placeholder=" "
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
            />
            <label htmlFor="confirmPassword">Confirm new password</label>
          </div>

          {/* Alert Messages */}
          {errorMsg && (
            <div className="text-sm text-rose-600 font-medium flex items-center gap-2 pt-1 animate-fade-in">
              <svg className="w-4 h-4 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
              {errorMsg}
            </div>
          )}

          {successMsg && (
            <div className="text-sm text-emerald-600 font-medium flex items-center gap-2 pt-1 animate-fade-in">
              <svg className="w-4 h-4 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              {successMsg}
            </div>
          )}

          {/* Footer Actions */}
          <div className="flex gap-3 pt-4 border-t border-ink-100">
            <button
              type="button"
              onClick={onClose}
              disabled={isLoading}
              className="flex-1 h-12 rounded-xl text-sm font-semibold text-ink-700 bg-ink-100 hover:bg-ink-150 transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading || !oldPassword || !newPassword || !confirmPassword}
              className="flex-1 h-12 btn-v2-primary text-sm font-semibold flex items-center justify-center gap-2"
            >
              {isLoading ? (
                <>
                  <svg className="w-4 h-4 animate-spin text-current" fill="none" viewBox="0 0 24 24">
                    <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" strokeOpacity="0.25" />
                    <path d="M12 2a10 10 0 0110 10" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
                  </svg>
                  Saving...
                </>
              ) : (
                "Change password"
              )}
            </button>
          </div>

        </form>
      </div>
    </div>
  );
};

export default ChangePasswordModal;
